import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { aiJobsAPI } from '../../api/aiJobsApi';
import {
    TableCellsIcon,
    ArrowPathIcon,
    FaceSmileIcon,
    FaceFrownIcon,
    MinusCircleIcon,
    CalendarDaysIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    EyeIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

const AIRecordAnalyses = () => {
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);

    // Tarih filtreleri - varsayılan son 30 gün
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const [startDate, setStartDate] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

    // Filtreler
    const [searchTerm, setSearchTerm] = useState('');
    const [sentimentFilter, setSentimentFilter] = useState('all');

    const fetchAnalyses = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await aiJobsAPI.getAnalyses(startDate, endDate);
            if (response.success) {
                setAnalyses(response.data || []);
            } else {
                setError(response.message || 'Veriler alınırken hata oluştu');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalyses();
    }, []);

    // Modal açıldığında scroll kilitle
    useEffect(() => {
        if (selectedAnalysis) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [selectedAnalysis]);

    // Filtrelenmiş analizler
    const filteredAnalyses = analyses.filter(item => {
        // Arama filtresi
        const searchMatch = searchTerm === '' ||
            item.agency?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.keywords?.some(k => k.toLowerCase().includes(searchTerm.toLowerCase())) ||
            item.ai_insights?.toLowerCase().includes(searchTerm.toLowerCase());

        // Duygu filtresi
        const sentimentMatch = sentimentFilter === 'all' || item.sentiment_label === sentimentFilter;

        return searchMatch && sentimentMatch;
    });

    const getSentimentBadge = (label, score) => {
        const config = {
            positive: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', icon: FaceSmileIcon },
            negative: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', icon: FaceFrownIcon },
            neutral: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', icon: MinusCircleIcon }
        };
        const c = config[label] || config.neutral;
        const Icon = c.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                <Icon className="w-4 h-4" />
                {score?.toFixed(2)}
            </span>
        );
    };

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                            <TableCellsIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Detailed Insights</h1>
                            <p className="text-gray-500 dark:text-gray-400 text-sm">Her görüşme kaydının detaylı AI analizi</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
                <div className="flex flex-wrap items-center gap-4">
                    {/* Tarih Filtreleri */}
                    <div className="flex items-center gap-2">
                        <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <span className="text-gray-400">-</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                    </div>

                    {/* Arama */}
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Acenta, ülke, kelime ara..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                        </div>
                    </div>

                    {/* Duygu Filtresi */}
                    <div className="flex items-center gap-2">
                        <FunnelIcon className="w-5 h-5 text-gray-400" />
                        <select
                            value={sentimentFilter}
                            onChange={(e) => setSentimentFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        >
                            <option value="all">Tüm Duygular</option>
                            <option value="positive">Olumlu</option>
                            <option value="neutral">Nötr</option>
                            <option value="negative">Olumsuz</option>
                        </select>
                    </div>

                    {/* Yenile Butonu */}
                    <button
                        onClick={fetchAnalyses}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                        <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        Yenile
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {/* Stats Bar */}
            <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Toplam Kayıt</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{filteredAnalyses.length}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <FaceSmileIcon className="w-4 h-4 text-green-500" /> Olumlu
                    </p>
                    <p className="text-2xl font-bold text-green-600">{filteredAnalyses.filter(a => a.sentiment_label === 'positive').length}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <MinusCircleIcon className="w-4 h-4 text-gray-500" /> Nötr
                    </p>
                    <p className="text-2xl font-bold text-gray-600">{filteredAnalyses.filter(a => a.sentiment_label === 'neutral').length}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <FaceFrownIcon className="w-4 h-4 text-red-500" /> Olumsuz
                    </p>
                    <p className="text-2xl font-bold text-red-600">{filteredAnalyses.filter(a => a.sentiment_label === 'negative').length}</p>
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center py-20">
                    <ArrowPathIcon className="w-8 h-8 text-green-600 animate-spin" />
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Veriler yükleniyor...</span>
                </div>
            )}

            {/* Table */}
            {!loading && filteredAnalyses.length === 0 ? (
                <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <TableCellsIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Kayıt bulunamadı</h3>
                    <p className="text-gray-500 dark:text-gray-400">
                        Seçilen tarih aralığında analiz edilmiş kayıt yok.
                    </p>
                </div>
            ) : !loading && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300 text-sm">Tarih</th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300 text-sm">Acenta</th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300 text-sm">Ülke</th>
                                    <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-300 text-sm">Duygu</th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300 text-sm">Kelimeler</th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300 text-sm">Lokasyonlar</th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300 text-sm">AI Insight</th>
                                    <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-300 text-sm">Detay</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredAnalyses.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-4 py-3 text-gray-900 dark:text-white whitespace-nowrap text-sm">{item.call_date}</td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-sm">{item.agency || '-'}</td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-sm">{item.country || '-'}</td>
                                        <td className="px-4 py-3 text-center">
                                            {getSentimentBadge(item.sentiment_label, item.sentiment_score)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {item.keywords?.slice(0, 3).map((kw, i) => (
                                                    <span key={i} className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-xs">
                                                        {kw}
                                                    </span>
                                                ))}
                                                {item.keywords?.length > 3 && (
                                                    <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded text-xs">
                                                        +{item.keywords.length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {item.locations_mentioned?.slice(0, 2).map((loc, i) => (
                                                    <span key={i} className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                                                        {loc}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs max-w-xs truncate" title={item.ai_insights}>
                                            {item.ai_insights?.substring(0, 80) || '-'}
                                            {item.ai_insights?.length > 80 && '...'}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button
                                                onClick={() => setSelectedAnalysis(item)}
                                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                title="Detayları Gör"
                                            >
                                                <EyeIcon className="w-5 h-5 text-gray-500 hover:text-indigo-600" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Detail Modal - React Portal ile sayfanın ortasına render */}
            {selectedAnalysis && ReactDOM.createPortal(
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setSelectedAnalysis(null);
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-[fadeIn_0.2s_ease-out]">
                        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between z-10">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                Analiz Detayı - {selectedAnalysis.call_date}
                            </h3>
                            <button
                                onClick={() => setSelectedAnalysis(null)}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <XMarkIcon className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            {/* Header Info */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                                    <p className="text-xs text-gray-500 mb-1">Acenta</p>
                                    <p className="font-semibold text-gray-900 dark:text-white">{selectedAnalysis.agency || '-'}</p>
                                </div>
                                <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                                    <p className="text-xs text-gray-500 mb-1">Ülke</p>
                                    <p className="font-semibold text-gray-900 dark:text-white">{selectedAnalysis.country || '-'}</p>
                                </div>
                                <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                                    <p className="text-xs text-gray-500 mb-1">Duygu Skoru</p>
                                    <div className="mt-1">
                                        {getSentimentBadge(selectedAnalysis.sentiment_label, selectedAnalysis.sentiment_score)}
                                    </div>
                                </div>
                            </div>

                            {/* Keywords */}
                            {selectedAnalysis.keywords?.length > 0 && (
                                <div>
                                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Anahtar Kelimeler</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedAnalysis.keywords.map((kw, i) => (
                                            <span key={i} className="px-3 py-1.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-sm font-medium">
                                                {kw}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Locations */}
                            {selectedAnalysis.locations_mentioned?.length > 0 && (
                                <div>
                                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Bahsedilen Lokasyonlar</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedAnalysis.locations_mentioned.map((loc, i) => (
                                            <span key={i} className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-medium">
                                                {loc}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Topics */}
                            {selectedAnalysis.topics?.length > 0 && (
                                <div>
                                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Konular</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedAnalysis.topics.map((topic, i) => (
                                            <span key={i} className="px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full text-sm font-medium">
                                                {topic}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* AI Insights */}
                            {selectedAnalysis.ai_insights && (
                                <div>
                                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">AI Insight</h4>
                                    <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl">
                                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{selectedAnalysis.ai_insights}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default AIRecordAnalyses;
